import React from "react";

const shimmerClassName = "animate-pulse rounded bg-gray-200";

const SkeletonBlock: React.FC<{ className: string }> = ({ className }) => (
  <div className={`${shimmerClassName} ${className}`} />
);

interface PublicGridSkeletonProps {
  count?: number;
  variant?: "course" | "university";
}

export const PublicGridSkeleton: React.FC<PublicGridSkeletonProps> = ({
  count = 6,
  variant = "course",
}) => {
  const items = Array.from({ length: count });

  if (variant === "university") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {items.map((_, index) => (
          <div
            key={`university-skeleton-${index}`}
            className="bg-white rounded-xl shadow-md border border-gray-100 p-6"
          >
            <div className="flex items-start space-x-4">
              <SkeletonBlock className="w-16 h-16 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-3">
                <SkeletonBlock className="h-6 w-3/4" />
                <SkeletonBlock className="h-4 w-1/2" />
                <SkeletonBlock className="h-4 w-2/3" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
              <SkeletonBlock className="h-4 w-1/4" />
              <SkeletonBlock className="h-10 w-28 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((_, index) => (
        <div
          key={`course-skeleton-${index}`}
          className="bg-white rounded-lg shadow-sm p-6 border border-gray-100"
        >
          <SkeletonBlock className="h-5 w-3/4 mb-4" />
          <SkeletonBlock className="h-4 w-full mb-2" />
          <SkeletonBlock className="h-4 w-2/3 mb-5" />
          <div className="flex items-center justify-between">
            <SkeletonBlock className="h-4 w-1/4" />
            <SkeletonBlock className="h-10 w-24 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
};

export const UniversityDetailSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50">
    <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-6">
            <div className="w-8 h-8 rounded mr-4 bg-white/20 animate-pulse" />
            <div className="h-6 w-32 rounded bg-white/20 animate-pulse" />
          </div>
          <div className="flex items-start space-x-6">
            <div className="w-20 h-20 rounded-xl bg-white/20 animate-pulse flex-shrink-0" />
            <div className="flex-1">
              <div className="h-10 w-2/3 rounded bg-white/20 animate-pulse mb-4" />
              <div className="h-6 w-1/3 rounded bg-white/20 animate-pulse mb-4" />
              <div className="h-20 max-w-xl rounded-xl bg-white/15 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white/90 border border-cyan-100 rounded-2xl p-4 sm:p-6 shadow-sm mb-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-2 flex-1">
                <SkeletonBlock className="h-5 w-40" />
                <SkeletonBlock className="h-4 w-64 max-w-full" />
              </div>
              <SkeletonBlock className="h-12 w-full sm:w-72 rounded-xl" />
            </div>
          </div>
          <PublicGridSkeleton count={6} variant="course" />
        </div>
      </div>
    </section>
  </div>
);

export const CourseDetailSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50">
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center space-x-4">
          <SkeletonBlock className="h-4 w-12" />
          <SkeletonBlock className="h-4 w-4" />
          <SkeletonBlock className="h-4 w-20" />
          <SkeletonBlock className="h-4 w-4" />
          <SkeletonBlock className="h-4 w-40" />
        </div>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="lg:hidden mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <SkeletonBlock className="h-7 w-4/5 mb-3" />
          <div className="space-y-2 mb-4">
            <SkeletonBlock className="h-4 w-2/3" />
            <SkeletonBlock className="h-4 w-1/2" />
          </div>
          <div className="rounded-lg border border-cyan-100 bg-gradient-to-r from-cyan-50 to-orange-50 p-4 mb-4">
            <SkeletonBlock className="h-7 w-28 mx-auto mb-2" />
            <SkeletonBlock className="h-4 w-24 mx-auto" />
          </div>
          <SkeletonBlock className="h-4 w-full mb-2" />
          <SkeletonBlock className="h-4 w-full mb-2" />
          <SkeletonBlock className="h-4 w-5/6" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          <div className="hidden lg:block bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-start justify-between mb-6 gap-6">
              <div className="flex-1">
                <SkeletonBlock className="h-9 w-3/4 mb-3" />
                <div className="flex flex-wrap gap-3">
                  <SkeletonBlock className="h-4 w-32" />
                  <SkeletonBlock className="h-4 w-24" />
                  <SkeletonBlock className="h-4 w-20" />
                </div>
              </div>
              <div className="w-32 flex-shrink-0">
                <SkeletonBlock className="h-8 w-28 mb-2 ml-auto" />
                <SkeletonBlock className="h-4 w-24 ml-auto" />
              </div>
            </div>
            <SkeletonBlock className="h-5 w-32 mb-3" />
            <SkeletonBlock className="h-4 w-full mb-2" />
            <SkeletonBlock className="h-4 w-full mb-2" />
            <SkeletonBlock className="h-4 w-5/6" />
          </div>

          <div className="space-y-4 sm:space-y-6 lg:hidden">
            {[0, 1].map((index) => (
              <div
                key={`mobile-info-${index}`}
                className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200"
              >
                <SkeletonBlock className="h-6 w-40 mb-4" />
                <div className="space-y-3">
                  {[0, 1, 2, 3].map((row) => (
                    <div
                      key={`mobile-row-${index}-${row}`}
                      className="flex justify-between gap-4"
                    >
                      <SkeletonBlock className="h-4 w-24" />
                      <SkeletonBlock className="h-4 w-32" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {[0, 1, 2, 3].map((index) => (
            <div
              key={`detail-section-${index}`}
              className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200"
            >
              <SkeletonBlock className="h-6 w-48 mb-4" />
              <div className="space-y-3">
                <SkeletonBlock className="h-4 w-full" />
                <SkeletonBlock className="h-4 w-full" />
                <SkeletonBlock className="h-4 w-4/5" />
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4 sm:space-y-6">
          {[0, 1, 2].map((index) => (
            <div
              key={`sidebar-card-${index}`}
              className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200"
            >
              <SkeletonBlock className="h-6 w-32 mb-4" />
              <div className="space-y-3">
                <SkeletonBlock className="h-4 w-full" />
                <SkeletonBlock className="h-4 w-5/6" />
                <SkeletonBlock className="h-10 w-full rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);